import Median from 'median-js-bridge';

/** Session guard: skip redundant bridge calls once the external id is linked. */
let externalIdLinked = false;

export interface OneSignalInfo {
  oneSignalId?: string;
  externalId?: string;
  subscription?: {
    id: string;
  };
}

/**
 * Ensures the Median bridge is ready before running async functions.
 * Executor form is required: the app targets ES2020, which predates
 * Promise.withResolvers.
 */
async function withBridgeReady<T>(fn: () => Promise<T>): Promise<T> {
  return new Promise((resolve, reject) => {
    Median.onReady(async () => {
      try {
        resolve(await fn());
      } catch (e) {
        reject(e);
      }
    });
  });
}

/**
 * Checks if the app is running in the native environment
 */
export function isNative(): boolean {
  return Median.isNativeApp?.() ?? false;
}

/**
 * Log in to OneSignal, binding the device to the admin user via external id
 */
export async function oneSignalLogin(
  userId: string,
): Promise<OneSignalInfo | null> {
  if (!isNative()) {
    console.warn('OneSignal login skipped — not in native app.');
    return null;
  }

  if (externalIdLinked) {
    console.warn('OneSignal login skipped — external id already set.');
    return null;
  }

  if (!userId) {
    console.warn('OneSignal login skipped — missing user id.', userId);
    return null;
  }

  return await withBridgeReady(async () => {
    try {
      Median.onesignal.externalUserId.set({ externalId: userId });
    } catch (e) {
      console.error(e);
    }

    externalIdLinked = true;
    return await Median.onesignal.info();
  });
}

/**
 * Log out from OneSignal
 */
export async function oneSignalLogout(): Promise<void> {
  if (!isNative()) {
    console.warn('OneSignal logout skipped — not in native app.');
    return;
  }

  await withBridgeReady(async () => {
    await Median.onesignal.externalUserId.remove();
    externalIdLinked = false;
  });
}

/**
 * Get OneSignal info
 */
export async function getInfo(): Promise<OneSignalInfo | null> {
  if (!isNative()) return null;
  return await withBridgeReady(() => Median.onesignal.info());
}
